import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Mail, MapPin, User, Calendar, Database, Cookie, Link as LinkIcon, FileText } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import styles from './DatenschutzPage.module.css';

export const DatenschutzPage: React.FC = () => {
    const { language } = useLanguage();

    const content = {
        de: {
            title: 'Datenschutzerklärung',
            date: 'Stand: 08.10.2025',
            scopeLabel: 'Geltungsbereich:',
            scopeText: 'Diese Datenschutzerklärung gilt für die Website https://www.integrovaai.de.',
            intro: 'Diese Datenschutzerklärung informiert Sie darüber, wie die Integrova AI GmbH (nachfolgend „wir" oder „uns") personenbezogene Daten beim Besuch unserer Website, bei der Kontaktaufnahme sowie bei der Online-Terminbuchung verarbeitet.',
            sections: [
                {
                    id: 'controller',
                    icon: User,
                    title: '1. Verantwortlicher',
                    content: (
                        <div className={styles.contactCard}>
                            <p><strong>Integrova AI GmbH</strong></p>
                            <p>Garmischer Str. 195</p>
                            <p>81377 München</p>
                            <p>Deutschland</p>
                            <br />
                            <p><Mail size={16} className={styles.inlineIcon} /> E-Mail (allgemein): <a href="mailto:info@integrovaai.de">info@integrovaai.de</a></p>
                            <p><Mail size={16} className={styles.inlineIcon} /> E-Mail (Datenschutz): <a href="mailto:privacy@integrovaai.de">privacy@integrovaai.de</a></p>
                            <br />
                            <p><strong>Vertretungsberechtigter Geschäftsführer:</strong><br />André Tietje</p>
                        </div>
                    )
                },
                {
                    id: 'purposes',
                    icon: Database,
                    title: '2. Zwecke, Datenkategorien und Rechtsgrundlagen',
                    content: (
                        <>
                            <h3>2.1 Besuch der Website / Server-Logfiles</h3>
                            <p><strong>Verarbeitete Daten:</strong><br />
                                IP-Adresse, Datum und Uhrzeit der Anfrage, Zeitzone, aufgerufene URL, Referrer-URL, Browsertyp und -version, Betriebssystem, HTTP-Statuscode, übertragene Datenmenge.</p>
                            <p><strong>Zweck:</strong><br />
                                Sicherstellung des technischen Betriebs, Gewährleistung der Sicherheit und Stabilität der Website, Fehleranalyse sowie Abwehr von Missbrauchs- und Angriffsszenarien.</p>
                            <p><strong>Rechtsgrundlage:</strong><br />
                                Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an einer sicheren und stabilen Website).</p>
                            <p><strong>Speicherdauer:</strong><br />
                                Server-Logfiles werden automatisch gelöscht, in der Regel nach 7–30 Tagen.</p>

                            <div className={styles.divider} />

                            <h3>2.2 Consent-Management (Cookie-Banner)</h3>
                            <p><strong>Verarbeitete Daten:</strong><br />
                                Ihre Cookie-Einwilligungsentscheidung (Consent-Status), Zeitstempel, ggf. pseudonyme Sitzungs- oder Gerätekennung.</p>
                            <p><strong>Zweck:</strong><br />
                                Dokumentation und Verwaltung Ihrer Einwilligungen gemäß den gesetzlichen Vorgaben.</p>
                            <p><strong>Rechtsgrundlage:</strong><br />
                                Art. 6 Abs. 1 lit. c DSGVO i. V. m. Art. 7 DSGVO sowie Art. 6 Abs. 1 lit. f DSGVO.</p>

                            <div className={styles.divider} />

                            <h3>2.3 Google Analytics (GA4) – nur mit Einwilligung</h3>
                            <p><strong>Anbieter:</strong><br />
                                Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland.</p>
                            <p><strong>Verarbeitete Daten:</strong><br />
                                Seitenaufrufe, Interaktionen, ungefähre Standortdaten, Geräte- und Browserinformationen, Referrer. Die IP-Adresse wird anonymisiert.</p>
                            <p><strong>Zweck:</strong><br />
                                Analyse des Nutzerverhaltens zur Optimierung unserer Website und unseres Online-Angebots.</p>
                            <p><strong>Rechtsgrundlage:</strong><br />
                                Art. 6 Abs. 1 lit. a DSGVO (Einwilligung).</p>
                            <p><strong>Drittlandübermittlung:</strong><br />
                                Eine Übermittlung von Daten in die USA kann nicht ausgeschlossen werden. Google verwendet hierfür EU-Standardvertragsklauseln (SCC).</p>
                            <p><strong>Widerruf:</strong><br />
                                Sie können Ihre Einwilligung jederzeit über das Cookie-Banner widerrufen.</p>

                            <div className={styles.divider} />

                            <h3>2.4 Online-Terminbuchung über Microsoft Bookings</h3>
                            <p><strong>Anbieter:</strong><br />
                                Microsoft Ireland Operations Limited, Dublin, Irland.</p>
                            <p><strong>Verarbeitete Daten:</strong><br />
                                Name, E-Mail-Adresse, Telefonnummer (optional), Terminzeitpunkt, Zeitzone, Angaben zum Anliegen.</p>
                            <p><strong>Zweck:</strong><br />
                                Organisation, Durchführung und Nachbereitung von Beratungsterminen.</p>
                            <p><strong>Rechtsgrundlage:</strong><br />
                                Art. 6 Abs. 1 lit. b DSGVO (vorvertragliche Maßnahmen/Vertragserfüllung).</p>
                            <p><strong>Hinweis:</strong><br />
                                Microsoft verwendet EU-Standardvertragsklauseln für eventuelle Drittlandtransfers (USA).</p>

                            <div className={styles.divider} />

                            <h3>2.5 Kontaktaufnahme</h3>
                            <p><strong>Verarbeitete Daten:</strong><br />
                                Name, Kontaktdaten, Kommunikationsinhalte.</p>
                            <p><strong>Zweck:</strong><br />
                                Bearbeitung von Anfragen, geschäftliche Kommunikation.</p>
                            <p><strong>Rechtsgrundlage:</strong><br />
                                Art. 6 Abs. 1 lit. b oder f DSGVO.</p>
                            <p><strong>Speicherdauer:</strong><br />
                                Bis zum Abschluss der Anfrage oder gemäß gesetzlicher Fristen (6–10 Jahre nach HGB/AO).</p>
                        </>
                    )
                },
                {
                    id: 'recipients',
                    icon: MapPin,
                    title: '3. Empfänger / Kategorien von Empfängern',
                    content: (
                        <>
                            <ul className={styles.bulletList}>
                                <li><strong>Hosting & CDN:</strong> Hostinger (EU-Server)</li>
                                <li><strong>Microsoft:</strong> Microsoft 365 / Bookings</li>
                                <li><strong>Google:</strong> Google Analytics (nur bei Einwilligung)</li>
                            </ul>
                            <p>Alle Dienstleister sind vertraglich als Auftragsverarbeiter gemäß Art. 28 DSGVO eingebunden.</p>
                        </>
                    )
                },
                {
                    id: 'transfers',
                    icon: MapPin,
                    title: '4. Drittlandübermittlungen',
                    content: (
                        <p>Bei der Nutzung von Google- und Microsoft-Diensten kann eine Übermittlung personenbezogener Daten in Drittländer (insbesondere USA) erfolgen. In diesen Fällen kommen EU-Standardvertragsklauseln (SCC) zum Einsatz.</p>
                    )
                },
                {
                    id: 'storage',
                    icon: Calendar,
                    title: '5. Speicherdauer',
                    content: (
                        <p>Personenbezogene Daten werden nur so lange verarbeitet, wie dies für die jeweiligen Zwecke erforderlich ist oder gesetzliche Aufbewahrungspflichten bestehen. Danach erfolgt die Löschung oder Anonymisierung.</p>
                    )
                },
                {
                    id: 'provision',
                    icon: FileText,
                    title: '6. Pflicht zur Bereitstellung',
                    content: (
                        <p>Die Bereitstellung personenbezogener Daten ist für die Nutzung der Website grundsätzlich nicht erforderlich. Für Terminbuchungen und Kontaktanfragen sind bestimmte Angaben notwendig; ohne diese kann die jeweilige Leistung nicht erbracht werden.</p>
                    )
                },
                {
                    id: 'automated',
                    icon: Database,
                    title: '7. Automatisierte Entscheidungsfindung',
                    content: (
                        <>
                            <p>Eine automatisierte Entscheidungsfindung einschließlich Profiling findet nicht statt.</p>
                            <p>Es werden keine personenbezogenen Daten an externe KI-Dienste ohne Rechtsgrundlage übermittelt.</p>
                        </>
                    )
                },
                {
                    id: 'rights',
                    icon: Shield,
                    title: '8. Rechte der betroffenen Personen',
                    content: (
                        <>
                            <p>Sie haben das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit sowie Widerspruch gemäß Art. 15–21 DSGVO.</p>
                            <p>Sofern die Verarbeitung auf Ihrer Einwilligung beruht, können Sie diese jederzeit widerrufen.</p>
                            <p><strong>Zuständige Aufsichtsbehörde:</strong><br />
                                Bayerisches Landesamt für Datenschutzaufsicht (BayLDA)<br />
                                <a href="https://www.lda.bayern.de" target="_blank" rel="noopener noreferrer">www.lda.bayern.de</a></p>
                        </>
                    )
                },
                {
                    id: 'cookies',
                    icon: Cookie,
                    title: '9. Cookies',
                    content: (
                        <>
                            <p><strong>Erforderliche Cookies:</strong> Technisch notwendig.</p>
                            <p><strong>Analyse-Cookies:</strong> Google Analytics (nur mit Einwilligung).</p>
                            <p>Details entnehmen Sie bitte dem Cookie-Banner.</p>
                        </>
                    )
                },
                {
                    id: 'links',
                    icon: LinkIcon,
                    title: '10. Externe Links',
                    content: (
                        <p>Für Inhalte externer Links sind die jeweiligen Anbieter verantwortlich.</p>
                    )
                },
                {
                    id: 'changes',
                    icon: FileText,
                    title: '11. Änderungen',
                    content: (
                        <p>Wir behalten uns vor, diese Datenschutzerklärung anzupassen. Maßgeblich ist die aktuell online verfügbare Fassung.</p>
                    )
                }
            ]
        },
        en: {
            title: 'Privacy Policy',
            date: 'Last updated: 08.10.2025',
            scopeLabel: 'Scope:',
            scopeText: 'This Privacy Policy applies to the website https://www.integrovaai.de.',
            intro: 'This Privacy Policy informs you about how Integrova AI GmbH (hereinafter "we" or "us") processes personal data when you visit our website, contact us, or book appointments online.',
            sections: [
                {
                    id: 'controller',
                    icon: User,
                    title: '1. Controller',
                    content: (
                        <div className={styles.contactCard}>
                            <p><strong>Integrova AI GmbH</strong></p>
                            <p>Garmischer Str. 195</p>
                            <p>81377 Munich</p>
                            <p>Germany</p>
                            <br />
                            <p><Mail size={16} className={styles.inlineIcon} /> Email (General): <a href="mailto:info@integrovaai.de">info@integrovaai.de</a></p>
                            <p><Mail size={16} className={styles.inlineIcon} /> Email (Privacy): <a href="mailto:privacy@integrovaai.de">privacy@integrovaai.de</a></p>
                            <br />
                            <p><strong>Authorized Managing Director:</strong><br />André Tietje</p>
                        </div>
                    )
                },
                {
                    id: 'purposes',
                    icon: Database,
                    title: '2. Purposes, Data Categories, and Legal Bases',
                    content: (
                        <>
                            <h3>2.1 Website Visit / Server Logfiles</h3>
                            <p><strong>Processed Data:</strong><br />
                                IP address, date/time, timezone, URL, referrer, browser type/version, OS, status code, data volume.</p>
                            <p><strong>Purpose:</strong><br />
                                Ensuring technical operation, security, and stability; error analysis; defense against misuse.</p>
                            <p><strong>Legal Basis:</strong><br />
                                Art. 6(1)(f) GDPR (Legitimate interest in secure and stable website operation).</p>
                            <p><strong>Storage Duration:</strong><br />
                                Logfiles are deleted automatically, typically after 7–30 days.</p>

                            <div className={styles.divider} />

                            <h3>2.2 Consent Management (Cookie Banner)</h3>
                            <p><strong>Processed Data:</strong><br />
                                Consent status, timestamp, potentially pseudonymous session/device ID.</p>
                            <p><strong>Purpose:</strong><br />
                                Documentation and management of consents regarding legal requirements.</p>
                            <p><strong>Legal Basis:</strong><br />
                                Art. 6(1)(c) GDPR (Legal obligation) and Art. 6(1)(f) GDPR.</p>

                            <div className={styles.divider} />

                            <h3>2.3 Google Analytics (GA4) – only with consent</h3>
                            <p><strong>Provider:</strong><br />
                                Google Ireland Limited, Dublin, Ireland.</p>
                            <p><strong>Processed Data:</strong><br />
                                Page views, interactions, approximate location, device/browser info. IP address is anonymized.</p>
                            <p><strong>Purpose:</strong><br />
                                Analysis of user behavior to optimize our offer.</p>
                            <p><strong>Legal Basis:</strong><br />
                                Art. 6(1)(a) GDPR (Consent).</p>
                            <p><strong>Third Country Transfer:</strong><br />
                                Transfer to the USA cannot be ruled out. Google uses EU Standard Contractual Clauses (SCCs).</p>
                            <p><strong>Withdrawal:</strong><br />
                                You can withdraw consent at any time via the cookie banner.</p>

                            <div className={styles.divider} />

                            <h3>2.4 Online Appointment Booking (Microsoft Bookings)</h3>
                            <p><strong>Provider:</strong><br />
                                Microsoft Ireland Operations Limited, Dublin, Ireland.</p>
                            <p><strong>Processed Data:</strong><br />
                                Name, email, phone (optional), appointment time, timezone, request details.</p>
                            <p><strong>Purpose:</strong><br />
                                Organization and execution of consultation appointments.</p>
                            <p><strong>Legal Basis:</strong><br />
                                Art. 6(1)(b) GDPR (Contract performance/pre-contractual measures).</p>
                            <p><strong>Note:</strong><br />
                                Microsoft uses EU SCCs for potential third-country transfers.</p>

                            <div className={styles.divider} />

                            <h3>2.5 Contact</h3>
                            <p><strong>Processed Data:</strong><br />
                                Name, contact details, communication content.</p>
                            <p><strong>Purpose:</strong><br />
                                Handling inquiries, business communication.</p>
                            <p><strong>Legal Basis:</strong><br />
                                Art. 6(1)(b) or (f) GDPR.</p>
                            <p><strong>Storage Duration:</strong><br />
                                Until completion of the inquiry or per statutory retention periods (6–10 years).</p>
                        </>
                    )
                },
                {
                    id: 'recipients',
                    icon: MapPin,
                    title: '3. Recipients / Categories of Recipients',
                    content: (
                        <>
                            <ul className={styles.bulletList}>
                                <li><strong>Hosting & CDN:</strong> Hostinger (EU servers)</li>
                                <li><strong>Microsoft:</strong> Microsoft 365 / Bookings</li>
                                <li><strong>Google:</strong> Google Analytics (only with consent)</li>
                            </ul>
                            <p>All service providers are bound by Data Processing Agreements (DPA) acc. to Art. 28 GDPR.</p>
                        </>
                    )
                },
                {
                    id: 'transfers',
                    icon: MapPin,
                    title: '4. Third Country Transfers',
                    content: (
                        <p>When using Google and Microsoft services, data may be transferred to third countries (esp. USA). In these cases, EU Standard Contractual Clauses (SCCs) are used to ensure an adequate level of protection.</p>
                    )
                },
                {
                    id: 'storage',
                    icon: Calendar,
                    title: '5. Storage Duration',
                    content: (
                        <p>Personal data is processed only as long as necessary for the respective purposes or as required by law. Afterward, it is deleted or anonymized.</p>
                    )
                },
                {
                    id: 'provision',
                    icon: FileText,
                    title: '6. Obligation to Provide Data',
                    content: (
                        <p>Provision of data is generally not required to use the website. For bookings and contact requests, certain information is necessary to provide the service.</p>
                    )
                },
                {
                    id: 'automated',
                    icon: Database,
                    title: '7. Automated Decision-Making',
                    content: (
                        <>
                            <p>No automated decision-making or profiling takes place.</p>
                            <p>No personal data is transferred to external AI services without legal basis.</p>
                        </>
                    )
                },
                {
                    id: 'rights',
                    icon: Shield,
                    title: '8. Rights of Data Subjects',
                    content: (
                        <>
                            <p>You have the right to access, rectification, erasure, restriction of processing, data portability, and objection (Art. 15–21 GDPR).</p>
                            <p>If processing is based on consent, you may withdraw it at any time.</p>
                            <p><strong>Supervisory Authority:</strong><br />
                                Bayerisches Landesamt für Datenschutzaufsicht (BayLDA)<br />
                                <a href="https://www.lda.bayern.de" target="_blank" rel="noopener noreferrer">www.lda.bayern.de</a></p>
                        </>
                    )
                },
                {
                    id: 'cookies',
                    icon: Cookie,
                    title: '9. Cookies',
                    content: (
                        <>
                            <p><strong>Essential Cookies:</strong> Technically necessary.</p>
                            <p><strong>Analysis Cookies:</strong> Google Analytics (only with consent).</p>
                            <p>You can manage your preferences via the cookie banner.</p>
                        </>
                    )
                },
                {
                    id: 'links',
                    icon: LinkIcon,
                    title: '10. External Links',
                    content: (
                        <p>Providers are responsible for the content and privacy practices of linked external websites.</p>
                    )
                },
                {
                    id: 'changes',
                    icon: FileText,
                    title: '11. Changes',
                    content: (
                        <p>We reserve the right to update this Privacy Policy. The version currently available online is valid.</p>
                    )
                }
            ]
        }
    };

    const t = content[language];

    return (
        <div className={styles.page}>
            <section className={styles.hero}>
                <div className={styles.hero__container}>
                    <motion.div
                        className={styles.hero__icon}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 200 }}
                    >
                        <Shield size={40} />
                    </motion.div>
                    <motion.h1
                        className={styles.hero__title}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.15 }}
                    >
                        {t.title}
                    </motion.h1>
                    <motion.p
                        className={styles.hero__date}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.15, delay: 0 }}
                    >
                        {t.date}
                    </motion.p>
                </div>
            </section>

            <section className={styles.content}>
                <div className={styles.container}>
                    <motion.div
                        className={styles.intro}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.15, delay: 0 }}
                    >
                        <h2>{t.scopeLabel}</h2>
                        <p>{t.scopeText}</p>
                        <p className={styles.introText}>{t.intro}</p>
                    </motion.div>

                    {t.sections.map((section) => {
                        const Icon = section.icon;
                        return (
                            <motion.article
                                key={section.id}
                                className={styles.section}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.15 }}
                            >
                                <div className={styles.sectionHeader}>
                                    <Icon size={24} />
                                    <h2>{section.title}</h2>
                                </div>
                                <div className={styles.sectionContent}>
                                    {section.content}
                                </div>
                            </motion.article>
                        );
                    })}
                </div>
            </section>
        </div>
    );
};

export default DatenschutzPage;
